import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCookies } from "react-cookie";
import { refreshToken } from "../../api";
import { useInitializeApp } from "../../hooks";
import Context from "./Context";
import { convertCookies, REFRESH_KEY, ACCESS_KEY } from "./utils";

let token_expiration_time;

const AuthenticationProvider = ({ children }) => {
  const { setInitialData } = useInitializeApp();
  const [cookies, setCookie, removeCookie] = useCookies(ACCESS_KEY);
  const [token, setToken] = useState({
    refresh: convertCookies(cookies[REFRESH_KEY]),
    access: convertCookies(cookies[ACCESS_KEY]),
  });
  const queryClient = useQueryClient();

  const handleSignIn = useCallback(
    ({ refresh, access }) => {
      setCookie(REFRESH_KEY, refresh, { path: "/" });
      setCookie(ACCESS_KEY, access, { path: "/" });
      setToken({ refresh, access });
    },
    [setCookie, setToken]
  );
  const handleSignOut = useCallback(() => {
    queryClient.clear();
    setInitialData();
    removeCookie(REFRESH_KEY, { path: "/" });
    removeCookie(ACCESS_KEY, { path: "/" });
    setToken({});
  }, [removeCookie, setToken, queryClient, setInitialData]);

  const value = useMemo(
    () => ({
      isLoggedIn: Boolean(token.access) && Boolean(token.refresh),
      signIn: handleSignIn,
      signOut: handleSignOut,
    }),
    [token, handleSignIn, handleSignOut]
  );

  const { failureCount } = useQuery({
    queryKey: ["refreshToken"],
    queryFn: refreshToken,
    refetchInterval: () => (token_expiration_time ? token_expiration_time - 20 : 550) * 1000,
    enabled: Boolean(token.access) && Boolean(token.refresh),
    refetchIntervalInBackground: true,
    onSuccess: ({ data }) => {
      token_expiration_time = data.expires_in;
      handleSignIn({
        refresh: data.refresh_token,
        access: data.access_token,
      });
    },
  });
  useEffect(() => {
    if (failureCount >= 3) {
      handleSignOut();
    }
  }, [failureCount, handleSignOut]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default AuthenticationProvider;
